import { getCustomerOrdersById } from 'api/customers';
import NewTabLink from 'components/NewTabLink';
import PaginateTable from 'components/PaginateTable';
import { PERMISSIONS } from 'constants/permissions';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import usePagination from 'hooks/usePagination';
import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import { errorHandling, getData, priceTransformer } from 'utils';
import { captureException } from 'utils/errors';
import { StyledTable } from '../Overview/Table/styles';

function PromotionsTab({ customer }) {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const hasCustomerAccess = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);

  const getPromotionOrders = (page = 1) => {
    if (!hasCustomerAccess) return;
    setLoading(true);
    getCustomerOrdersById(customer.id, page, { has_coupon: true })
      .then((res) => {
        const data = getData(res);
        const results = data?.data?.results ?? data?.results ?? [];
        setOrders(Array.isArray(results) ? results : []);
        setPagination({
          count: getData(res)?.count,
          next: getData(res)?.next,
          previous: getData(res)?.previous,
        });
        setLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setLoading(false);
        errorHandling(e);
      });
  };

  const { pagination, setPagination, paginationEl } = usePagination(
    getPromotionOrders,
    loading,
  );

  useEffect(() => {
    if (hasCustomerAccess && customer?.id) {
      getPromotionOrders(1);
    }
  }, [hasCustomerAccess, customer?.id]);

  if (!hasCustomerAccess) {
    return null;
  }

  const base = (process.env.REACT_APP_BASENAME || '').replace(/\/$/, '');

  return (
    <div className="table-box">
      <div className="details-title">Promotion usage history</div>
      <StyledTable>
        <PaginateTable
          loading={loading}
          headers={[
            { title: 'Code used', width: '140px' },
            { title: 'Discount', width: '100px' },
            { title: 'Order', width: '120px' },
            { title: 'Order total', width: '120px' },
            { title: 'Date', width: '140px' },
          ]}
          dataArray={orders}
          rowCreators={[
            (row) => (
              <div className="strong">{row.coupon_code ?? '-'}</div>
            ),
            (row) => (
              <div className="strong">{priceTransformer(row.coupon_amount ?? 0)}</div>
            ),
            (row) => (
              <div>
                {row.id ? (
                  <NewTabLink
                    label={`#${row.number ?? row.id}`}
                    link={`${window.location.origin}${base}/console/orders/${row.id}`}
                  />
                ) : (
                  '-'
                )}
              </div>
            ),
            (row) => (
              <div className="strong">{priceTransformer(row.value)}</div>
            ),
            (row) => (
              <div>{row.created_at ? dayjs(row.created_at).format('MMM D, YYYY hh:mm A') : '-'}</div>
            ),
          ]}
          noRecord="No promotion usage recorded."
          onClick={(record) => {
            if (record?.id) {
              const orderUrl = `${window.location.origin}${base}/console/orders/${record.id}`;
              window.open(orderUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          newRowClasses="new-row"
          dataPaging={pagination}
        />
      </StyledTable>
      <div className="pagination">{paginationEl}</div>
    </div>
  );
}

export default PromotionsTab;
